/**
 * Person-facing audit labels (TMMAPP-0022, design §11.7). Unknown `action` / `entityType`
 * values fall back to a humanised token so a new backend value never blanks a row.
 */
import type { ActorKind } from '@/api/audit';

export function humanizeToken(token: string): string {
  const words = token.split(/[_-]+/).filter((part) => part.length > 0);
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0 ? `${lower.charAt(0).toUpperCase()}${lower.slice(1)}` : lower;
    })
    .join(' ');
}

export type ActionFamily =
  | 'ledger'
  | 'virtualAccounts'
  | 'plans'
  | 'entitlements'
  | 'holdings'
  | 'notifications'
  | 'review'
  | 'exports'
  | 'settings';

export interface ActionGroup {
  family: ActionFamily;
  actions: readonly string[];
}

/** Labelled person-facing families from §11.7 plus holdings / notification rules / review / exports / settings. */
export const ACTION_GROUPS: readonly ActionGroup[] = [
  {
    family: 'ledger',
    actions: [
      'ACCOUNT_CREATED',
      'ACCOUNT_UPDATED',
      'ACCOUNT_OPENING_CHANGED',
      'ACCOUNT_OPENING_CONFIRMED',
      'BALANCE_CHECK_RECORDED',
      'BALANCE_CHECK_RESOLVED',
      'BALANCE_CHECK_DISMISSED',
      'ACCOUNT_CLOSED',
      'PERMISSION_GRANTED',
      'PERMISSION_REVOKED',
      'IMPORT_COMMITTED',
      'TRANSACTION_POSTED',
      'TRANSACTION_VOIDED',
      'ALLOCATION_POSTED',
      'ALLOCATION_REVERSED',
      'CATEGORY_CHANGED',
      'ALLOCATION_RESPLIT',
      'ALLOCATION_ALTERNATE_NAME_CHANGED',
      'CATEGORY_CREATED',
      'CATEGORY_UPDATED',
      'CATEGORY_ARCHIVED',
      'RULE_PARTIAL',
      'RULE_FIRED',
      'RULE_CREATED',
      'RULE_UPDATED',
      'RULE_DELETED',
      'RULES_REORDERED',
      'RULE_AUTHORITY_LOST',
      // TMMAPP-0113 (the 0103 follow-up): transfers, reimbursements, counterparties, the history's
      // proposals, the budget start, account / category order, and bank connections.
      'TRANSFER_CLASSIFIED',
      'TRANSFER_CLEARED',
      'TRANSFER_PAIRED',
      // TMMAPI-0165: claiming own-account wires, and the pattern alias a payee-from-wire writes.
      'TRANSFER_LINES_CLAIMED',
      'PAYEE_ALIAS_CREATED',
      'PAYEE_ALIAS_APPLIED',
      'REIMBURSEMENT_LINKED',
      'REIMBURSEMENT_CONFIRMED',
      'REIMBURSEMENT_REJECTED',
      'REIMBURSEMENT_UNLINKED',
      // TMMAPI-0192 (TMMAPP-0163 re-vendor).
      'REIMBURSEMENT_WITHDRAWN',
      'PAYEE_CREATED',
      'PAYEE_UPDATED',
      'PAYEE_ARCHIVED',
      'PAYEE_MERGED',
      'PAYEE_ORDER_CHANGED',
      'PAYEE_ALIAS_ADDED',
      'PAYEE_ALIAS_REMOVED',
      'PAYEE_ALIAS_MOVED',
      'PAYEE_NAMED_ON_LINE',
      'PAYEE_CLEARED_ON_LINE',
      'PAYEE_AI_EXCLUSION_SET',
      'PAYEE_SUGGESTION_ACCEPTED',
      'PAYEE_SUGGESTION_REJECTED',
      'PAYEE_SUGGESTION_ACCEPT_ALL',
      'PAYEE_CATEGORY_DEFAULT_SET',
      'PAYEE_CATEGORY_DEFAULT_CLEARED',
      'GENESIS_PROPOSAL_ACCEPTED',
      'GENESIS_PROPOSAL_DISMISSED',
      'GENESIS_PROPOSAL_ACCEPT_ALL',
      'BUDGET_START_SET',
      'ACCOUNTS_REORDERED',
      'CATEGORIES_REORDERED',
      'CONNECTOR_LINKED',
      'CONNECTOR_MAPPED',
      'CONNECTOR_UNMAPPED',
      'CONNECTOR_REMOVED',
      'CONNECTOR_UPDATE_MODE',
      // TMMAPI-0182 U2 (TMMAPP-0142 J9).
      'ACCOUNT_OPENING_DERIVED',
      // TMMAPI-0191 (TMMAPP-0162 item 6).
      'ACCOUNT_OPENING_MOVED',
      // TMMAPI-0195 U2 and U3 (TMMAPP-0163 U2 re-vendor).
      'ACCOUNT_INSTITUTION_CONFIRMED',
      'ACCOUNT_INSTITUTION_PROPOSAL_DISMISSED',
      'PAYEE_TEXT_KEPT_UNNAMED',
      'PAYEE_TEXT_RESTORED',
      'PAYEE_ALIAS_UPDATED',
      'PAYEE_ALIAS_DELETED',
      'PAYEE_SUGGESTION_RESTORED',
      'ALLOCATION_RULE_TARGETS_REMOVED',
      'CONNECTOR_REMOVAL_ABANDONED',
      'GENESIS_DISMISSED',
      'GENESIS_RESUMED',
    ],
  },
  {
    family: 'virtualAccounts',
    actions: [
      'VIRTUAL_ACCOUNT_CREATED',
      'VIRTUAL_ACCOUNT_RENAMED',
      'VIRTUAL_ACCOUNT_DELETED',
      'VIRTUAL_ACCOUNT_ARCHIVED',
      'VIRTUAL_ACCOUNT_RESTORED',
      'SHARE_CREATED',
      'SHARE_ACCEPTED',
      'SHARE_REVOKED',
      'SHARE_LEFT',
      // TMMAPI-0146: a share's role, and the erasure records on either side of a contribution.
      'SHARE_ROLE_CHANGED',
      'CONTRIBUTIONS_REMOVED',
      'VIRTUAL_ACCOUNT_REMOVED_BY_OWNER_ERASURE',
      'ENVELOPE_CREATED',
      'ENVELOPE_RENAMED',
      'ENVELOPE_TARGET_CHANGED',
      'ENVELOPE_BUDGET_CHANGED',
      'ENVELOPE_MOVED',
      'ENVELOPE_ARCHIVED',
      'ENVELOPE_UNARCHIVED',
      'VIRTUAL_ACCOUNTS_REORDERED',
      'ENVELOPES_REORDERED',
      // TMMAPI-0182 U2 (TMMAPP-0142 J9).
      'VIRTUAL_ACCOUNT_REMOVED_BY_OWNER_RESET',
      'ASSISTANT_GRANT_CHANGED',
      // TMMAPI-0186 U1.
      'ASSISTANT_MEMBER_LIMIT_CHANGED',
    ],
  },
  {
    family: 'plans',
    actions: [
      'SERIES_CREATED',
      'SERIES_UPDATED',
      'SERIES_SPLIT',
      'SERIES_CANCELLED',
      'OCCURRENCE_MODIFIED',
      'OCCURRENCE_CANCELLED',
      'OCCURRENCE_RESTORED',
      'SLICE_CHANGED',
      'OCCURRENCE_MATCHED',
      'OCCURRENCE_UNMATCHED',
      // TMMAPI-0202 (MM-D-153): "that's all" on a part-paid occurrence, and its reopening.
      'PLANNED_OCCURRENCE_CLOSED_SHORT',
      'PLANNED_OCCURRENCE_REOPENED',
      'OCCURRENCE_POSTED',
      'OCCURRENCE_MISSED',
      'OCCURRENCE_SUPERSEDED',
      'SERIES_ENDED_FROM',
      'COVERAGE_ADVANCED',
      'PLANNED_EXPENSE_CREATED',
      'PLANNED_EXPENSE_UPDATED',
      'PLANNED_EXPENSE_DELETED',
      'PLANNED_EXPENSES_SPREAD',
      'PLAN_LINE_OCCURRENCE_DISMISSED',
      'PLAN_LINE_OCCURRENCE_RESTORED',
      // TMMAPI-0148: an expected item marked received (or its undo), and the split rules (MM-D-122, 123).
      'PLAN_LINE_SETTLED',
      'SPLIT_RULE_CREATED',
      'SPLIT_RULE_UPDATED',
      'SPLIT_RULE_DELETED',
      'SPLIT_RULES_REORDERED',
      'SPLIT_RULE_AUTHORITY_LOST',
      'PLAN_AUTHORITY_LOST',
      'MEETING_CREATED',
      'MEETING_UPDATED',
      'MEETING_CANCELLED',
      'MEETING_OCCURRENCE_STARTED',
      'MEETING_OCCURRENCE_ENDED',
      'MEETING_OCCURRENCE_SKIPPED',
      'NOTE_CREATED',
      'NOTE_UPDATED',
      'VIRTUAL_ACCOUNT_SUMMARY_UPDATED',
      'INVITE_SENT',
      'INVITE_CANCELLED',
      'RECORDING_ADDED',
      'RECORDING_REMOVED',
      'SUMMARY_REQUESTED',
      'SUMMARY_READY',
      'SUMMARY_FAILED',
      'ITEM_CREATED',
      'ITEM_CHANGED',
      'ITEM_CONFIRMED',
      // TMMAPI-0182 U2 (TMMAPP-0142 J9).
      'PLAN_MATCH_DISMISSED',
      'PLAN_MATCH_UNDISMISSED',
    ],
  },
  {
    family: 'entitlements',
    actions: [
      'SUBSCRIPTION_STARTED',
      'SUBSCRIPTION_ENDED',
      'SUBSCRIPTION_CANCELLED',
      'SUBSCRIPTION_COMPED',
      'SUBSCRIPTION_UNCOMPED',
      'SUBSCRIPTION_PLAN_CHANGED',
      'SEAT_STARTED',
      'SEAT_ENDING',
      'SEAT_ENDED',
      'PAYMENT_METHOD_UPDATED',
      'USER_FROZEN',
      'USER_UNFROZEN',
      'ACCOUNT_LOCKED',
      'ACCOUNT_UNLOCKED',
      'DELETION_REQUESTED',
      'DELETION_CANCELLED',
      'SUBSCRIPTION_INTERVAL_CHANGED',
      // TMMAPI-0151: the person's AI wallet.
      'AI_WALLET_CHARGE_RECORDED',
      'AI_WALLET_CREDITED',
      'AI_WALLET_AUTO_RECHARGE_CHANGED',
      'AI_WALLET_FORFEITED',
      // The person's own billing and support records (owner = the person at the pin; QA round 1).
      'SUBSCRIPTION_CREATION_ABANDONED',
      'SUBSCRIPTION_OFFER_CHANGED',
      'PROMOTION_REDEEMED',
      'SUBSCRIPTION_RECONCILED',
      'STATEMENT_CREATED',
      'STATEMENT_PAID',
      'STATEMENT_ZERO',
      'STATEMENT_ACTION_REQUIRED',
      'STATEMENT_FAILED',
      'STATEMENT_ATTENTION',
      'STATEMENT_WAIVED',
      'STATEMENT_RETRIED',
      'PENDING_SUBSCRIPTION_COMPLETED',
      'AI_SUSPENSION_SET',
      'AI_SUSPENSION_CLEARED',
      'DELETION_REQUESTED_BY_SUPPORT',
      'DELETION_CANCELLED_BY_SUPPORT',
      'ACCOUNT_ERASURE_FAILED',
      'REFERRAL_ATTRIBUTED',
      'REFERRAL_SUPERSEDED',
      // TMMAPI-0182 U2 (TMMAPP-0142 J9).
      'TICKET_FILED',
      // TMMAPI-0185 U1 (TMMAPP-0149 item 4): the member enum names these, so each is labelled.
      'PROMOTION_ENDED',
      'FEED_EVENT_SKIPPED',
      'ACCOUNT_ERASED',
      'ACCOUNT_ERASED_IMMEDIATELY',
      'CONTACT_SYNC_REPLAYED',
      'AFFILIATE_INVITED',
      'AFFILIATE_UPDATED',
      'AFFILIATE_APPROVED',
      'AFFILIATE_PAUSED',
      'AFFILIATE_RESUMED',
      'AFFILIATE_ENDED',
      'AFFILIATE_ERASED',
      'AFFILIATE_PAYOUT_HOLD_RELEASED',
      'TICKET_STATUS_CHANGED',
      'TICKET_UPDATE_POSTED',
    ],
  },
  {
    family: 'holdings',
    actions: [
      'HOLDING_CREATED',
      'HOLDING_UPDATED',
      'HOLDING_DISPOSED',
      'HOLDING_VALUED',
      'HOLDINGS_REORDERED',
    ],
  },
  {
    family: 'notifications',
    actions: [
      'NOTIFICATION_RULE_CREATED',
      'NOTIFICATION_RULE_UPDATED',
      'NOTIFICATION_RULE_DELETED',
      'NOTIFICATION_RULE_DISABLED',
    ],
  },
  {
    family: 'review',
    actions: ['REVIEW_DISMISSED'],
  },
  {
    family: 'exports',
    actions: ['EXPORT_PRODUCED'],
  },
  {
    family: 'settings',
    actions: [
      'USER_SETTINGS_CHANGED',
      'AUTOMATION_CHANGED',
      // TMMAPI-0182 U2 (TMMAPP-0142 J9): starting over, and the person's assistant sessions.
      'DATA_RESET',
      'ASSISTANT_SCOPE_CHANGED',
      'ASSISTANT_SESSION_DELETED',
      'ASSISTANT_PRIVATE_DATA_SHARED',
      'ASSISTANT_PRIVATE_DATA_REMOVED',
      // TMMAPI-0183 U1 (TMMAPP-0142 L6): the person's connected AI tools and their sessions.
      'AGENT_SESSION_GRANTED',
      'AGENT_SESSION_GRANT_CHANGED',
      'AGENT_SESSION_GRANT_REVOKED',
      'AGENT_CONNECTION_DISCONNECTED',
      // TMMAPI-0185 U1: "Not now" on a ticket the assistant drafted.
      'ASSISTANT_TICKET_DRAFT_DISMISSED',
    ],
  },
];

export const LABELLED_ACTIONS: readonly string[] = ACTION_GROUPS.flatMap((group) => group.actions);

/**
 * Person-facing `entityType` values seen on AuditRecorder / AuditQuery in tmm-api (no enum on
 * AuditEventView). Unknown types use `audit.entity.other`.
 */
export const ENTITY_TYPES: readonly string[] = [
  'account',
  'account_permission',
  'allocation',
  'allocation_rule',
  'audit',
  'billing_subscription',
  'connector_account',
  'connector_item',
  'virtual_account',
  'virtual_account_share',
  'virtual_account_summary',
  'entitlement',
  'holding',
  'import_batch',
  'meeting',
  'meeting_invite',
  'meeting_item',
  'meeting_recording',
  'meeting_summary',
  'notification_rule',
  'planned_occurrence',
  'planned_series',
  'review',
  'transaction',
  'user',
  'envelope',
  'envelope_category',
  'envelope_plan_line',
];

/**
 * Every `actorKind` a member's audit view serves (TMMAPP-0151 K5; the contract's enum since
 * TMMAPI-0185 U1). The record makes the compiler hold the list to the contract: a kind added or
 * removed there fails here until it is labelled or dropped.
 */
const ACTOR_KIND_SET: Readonly<Record<ActorKind, true>> = {
  USER: true,
  RULE: true,
  SYSTEM: true,
  AI: true,
  PLATFORM_SUPPORT: true,
  PRODUCT_ADMIN: true,
  AGENT: true,
  AFFILIATE: true,
};

export const ACTOR_KINDS = Object.keys(ACTOR_KIND_SET) as readonly ActorKind[];
